import * as React from 'react';
import type {TrackReferenceOrPlaceholder} from '@livekit/components-core';
import {
    TrackLoop,
    TrackRefContext,
    useGridLayout,
    usePagination,
    UseParticipantsOptions,
    useSwipe
} from "@livekit/components-react";
import {mergeProps} from "@livekit/components-react/src/utils";
import {ParticipantTileM} from "./ParticipantTileM";

/** @public */
export interface GridLayoutProps
    extends React.HTMLAttributes<HTMLDivElement>,
        Pick<UseParticipantsOptions, 'updateOnlyOn'> {
    children: React.ReactNode;
    tracks: TrackReferenceOrPlaceholder[];
    onSelectTrack: (arg0: TrackReferenceOrPlaceholder) => void;
    participants: any[];
}

/**
 * The `GridLayout` component displays the nested participants in a grid where every participants has the same size.
 * It also supports pagination if there are more participants than the grid can display.
 * @remarks
 * To ensure visual stability when tiles are reordered due to track updates,
 * the component uses the `useVisualStableUpdate` hook.
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <GridLayout tracks={tracks}>
 *     <ParticipantTile />
 *   </GridLayout>
 * <LiveKitRoom>
 * ```
 * @public
 */
export function GridLayoutM({tracks, onSelectTrack, participants, ...props}: GridLayoutProps) {
    const gridEl = React.createRef<HTMLDivElement>();

    const elementProps = React.useMemo(
        () => mergeProps(props, {className: 'lk-grid-layout'}),
        [props],
    );
    const {layout} = useGridLayout(gridEl, tracks.length);
    const pagination = usePagination(layout.maxTiles, tracks);

    useSwipe(gridEl, {
        onLeftSwipe: pagination.nextPage,
        onRightSwipe: pagination.prevPage,
    });

    return (
        <div ref={gridEl} data-lk-pagination={pagination.totalPageCount > 1} {...elementProps}>
            <TrackLoop tracks={tracks}>
                <TrackRefContext.Consumer>
                    {(trackRef) => trackRef &&
                        <ParticipantTileM participants={participants}
                                          onClick={() => onSelectTrack(trackRef)}
                                          trackRef={trackRef}/>}
                </TrackRefContext.Consumer>
            </TrackLoop>
        </div>
    );
}
